import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { parseGlass } from "@/lib/sections";
import { scaleParts, cellRect, visibleCells } from "@/lib/geometry";

let library = null;
export const setLibrary = (l) => { library = l; };
export const getLibrary = () => library;
export const subtypes = () => library?.subtypes || [];
export const subtypeOf = (item) => subtypes().find((s) => s.key === item?.iwin);
export const subtypeBySystem = (name) => subtypes().find((s) => s.system === (name || "").trim());

const kindOf = (op) => (op === "sliding_left" || op === "sliding_right" ? "S" : op === "fixed" || op === "louvre" ? "F" : op.startsWith("door") ? "D" : op === "awning" || op === "hopper" ? "A" : "C");
const mainRow = (item) => item.rows.reduce((b, h, i) => (h > item.rows[b] ? i : b), 0);

export function inferSubtype(item) {
  const main = item.cells[mainRow(item)], seq = main.map(kindOf).join("");
  const t = `${item.source_typology || ""} ${item.name || ""} ${item.notes || ""}`.toLowerCase();
  const std = subtypes().filter((s) => s.family !== "sliding_big");
  if (/railing|balustrade|guard ?rail/.test(t) && (seq === "FS" || seq === "SF")) return "s_single_railing";
  if (t.includes("corner") && /^S+$/.test(seq)) return "s_corner";
  const exact = std.find((s) => s.pattern.join() === main.join());
  if (exact) return exact.key;
  const byKind = std.find((s) => s.key !== "s_single_railing" && s.pattern.map(kindOf).join("") === seq);
  if (byKind) return byKind.key;
  if (/^S+$/.test(seq)) return { 2: "s_2t2s", 3: "s_3t3s", 4: "s_2t4s", 6: "s_3t6s" }[seq.length] || null;
  if (/^F+$/.test(seq)) return "fixed";
  if (/^[CF]+$/.test(seq)) return "casement";
  if (/^[AF]+$/.test(seq)) return "top_hung";
  if (/^[DF]+$/.test(seq)) return seq.split("D").length - 1 === 2 ? "door_double" : "door_single";
  return null;
}

const typologyFor = (s, item) => ({ sliding: item.height >= 2000 ? "sliding_door" : "sliding_window", fixed: "fixed_window", casement: "casement_window", door: s.pattern.length > 1 ? "hinged_door_double" : "hinged_door_single" }[s.kind] || (s.key === "top_hung" ? "top_hung_window" : "combination"));

export function specOf(s, item) {
  return {
    iwin: s.key, profile_system: s.system, frame_width: s.frame, mullion_width: s.mullion, sash_width: s.sash,
    frame_depth: item.flyscreen && s.flyscreen_depth ? s.flyscreen_depth : s.depth, wind_load: +Object.keys(s.limits)[0],
    interlock: s.interlocks.includes(item.interlock) ? item.interlock : s.interlocks[0] || null,
    railing: s.railing ? { ...s.railing, ...(item.railing || {}) } : undefined,
  };
}

export function applySubtype(item, key, { layout = true } = {}) {
  const s = subtypes().find((x) => x.key === key);
  if (!s) return item;
  let next = { ...item, ...specOf(s, item), typology: typologyFor(s, item), type: s.kind === "door" || (s.kind === "sliding" && item.height >= 2000) ? "door" : item.type };
  if (!s.railing) delete next.railing;
  if (layout) {
    const mr = mainRow(item), n = s.pattern.length;
    if (item.columns.length !== n || (item.spans || []).length) {
      next = { ...next, columns: scaleParts(Array(n).fill(1), item.width), spans: [], panel_glass: {} };
      next.cells = item.rows.map((_, r) => (r === mr ? [...s.pattern] : Array(n).fill("fixed")));
    } else next.cells = item.cells.map((row, r) => (r === mr ? [...s.pattern] : row));
  }
  return next;
}

const DENSITY = 2.5;
export function leafWeight(item, r, c) {
  const cr = cellRect(item, r, c), g = parseGlass(item._eff_glass?.[`${r}-${c}`] || item.panel_glass?.[`${r}-${c}`] || item.glass_spec);
  const glassMm = g.panes.reduce((a, b) => a + b, 0);
  return (cr.w * cr.h) / 1e6 * glassMm * DENSITY + ((2 * (cr.w + cr.h)) / 1000) * 1.6;
}

export function designChecks(item) {
  const s = subtypeOf(item);
  if (!s) return [];
  const out = [], wl = String(item.wind_load || Object.keys(s.limits)[0]);
  const lim = s.limits[wl] || s.limits[Object.keys(s.limits)[0]] || {};
  const add = (level, text) => out.push({ level, text });
  const ops = visibleCells(item).map((v) => ({ ...v, op: item.cells[v.r][v.c] }));
  const leaves = s.per_leaf ? ops.filter((o) => o.op !== "fixed").map((o) => ({ ...o, ...cellRect(item, o.r, o.c) })) : [{ w: item.width, h: item.height }];
  leaves.forEach((L, i) => {
    const who = s.per_leaf ? `Leaf ${i + 1} (${Math.round(L.w)} × ${Math.round(L.h)})` : `Overall ${item.width} × ${item.height}`;
    if (lim.wmax && L.w > lim.wmax) add("error", `${who}: width over ${lim.wmax} mm max at ${wl} Pa`);
    if (lim.hmax && L.h > lim.hmax) add("error", `${who}: height over ${lim.hmax} mm max at ${wl} Pa`);
    if (lim.wmin && L.w < lim.wmin) add("warn", `${who}: width under the ${lim.wmin} mm tested minimum`);
    if (lim.hmin && L.h < lim.hmin) add("warn", `${who}: height under the ${lim.hmin} mm tested minimum`);
  });
  const glazing = library?.glazing || {};
  const specs = [...new Set([item.glass_spec, ...Object.values(item.panel_glass || {}), ...Object.values(item.band_glass || {})].filter(Boolean))];
  specs.forEach((sp) => {
    const g = parseGlass(sp), dgu = g.panes.length > 1, G = glazing[dgu ? "DGU" : "SGU"];
    if (G && !G.glass.includes(Math.round(g.T))) add("error", `Glass "${sp}" = ${g.T} mm: no IWIN-S gasket (${dgu ? "DGU 32 mm groove" : "SGU 18 mm groove"} fits ${G.glass.join("/")} mm)`);
  });
  ops.filter((o) => o.op !== "fixed" && o.op !== "louvre").forEach((o) => {
    const kg = leafWeight(item, o.r, o.c);
    if (kg > s.max_leaf_kg) add("error", `Panel ${o.r + 1}-${o.c + 1}: approx. ${Math.round(kg)} kg exceeds ${s.max_leaf_kg} kg/leaf`);
  });
  if (s.kind === "sliding") {
    const max = Math.max(0, ...ops.filter((o) => o.op.startsWith("sliding")).map((o) => leafWeight(item, o.r, o.c)));
    add("info", `Heaviest shutter ≈ ${Math.round(max)} kg → ${max <= 80 ? "single" : max <= 150 ? "double" : "triple"} rollers`);
  }
  if (s.interlocks.length && item.interlock && !s.interlocks.includes(item.interlock)) add("error", `${item.interlock}-type interlock is not available for ${s.name}`);
  if (s.railing && item.railing && item.railing.height < 1100) add("error", "Railing height must not be less than 1100 mm");
  if (!out.some((o) => o.level !== "info")) add("ok", `Within IWIN-S ${s.name} limits at ${wl} Pa`);
  return out;
}

const imgCache = new Map(), pending = new Map();
export const drawingKey = (s) => `${s.drawing}|${s.page_no || 1}`;
export function loadDrawing(s) {
  const k = drawingKey(s);
  if (imgCache.has(k)) return Promise.resolve(imgCache.get(k));
  if (!pending.has(k)) {
    pending.set(k, api.get(`/library/drawings/${s.drawing}/image`, { params: { page: s.page_no || 1 }, responseType: "blob" })
      .then(({ data }) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => { imgCache.set(k, fr.result); res(fr.result); }; fr.readAsDataURL(data); }))
      .finally(() => pending.delete(k)));
  }
  return pending.get(k);
}
export function useDrawing(s) {
  const [src, setSrc] = useState(() => s && imgCache.get(drawingKey(s)));
  useEffect(() => {
    if (!s) return undefined;
    let on = true;
    loadDrawing(s).then((v) => on && setSrc(v)).catch(() => {});
    return () => { on = false; };
  }, [s?.drawing, s?.page_no]); // eslint-disable-line react-hooks/exhaustive-deps
  return src;
}
export const loadVectors = (s) => api.get(`/library/drawings/${s.drawing}/vectors`, { params: { page: s.page_no || 1 } }).then((r) => r.data);

const secCache = new Map(), secPending = new Map();
const toPaths = (data) => ({ ...data, details: data.details.map(({ layers, ...d }) => ({ ...d, paths: Object.entries(layers).map(([L, segs]) => [L, segs.map(([a, b, c, e]) => `M${a} ${b}L${c} ${e}`).join("")]) })) });
export function loadSections(s) {
  if (secCache.has(s.key)) return Promise.resolve(secCache.get(s.key));
  if (!secPending.has(s.key)) {
    secPending.set(s.key, api.get(`/library/sections/${s.key}`).then(({ data }) => { const v = toPaths(data); secCache.set(s.key, v); return v; })
      .finally(() => secPending.delete(s.key)));
  }
  return secPending.get(s.key);
}
export function useSections(s) {
  const [data, setData] = useState(() => s && secCache.get(s.key));
  useEffect(() => {
    if (!s) return undefined;
    let on = true;
    setData(secCache.get(s.key));
    loadSections(s).then((v) => on && setData(v)).catch(() => {});
    return () => { on = false; };
  }, [s?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}
